/**
 * WorkforceAdvisors — bank-persona interaction atlas §5 (A1–A5), stage 4 of
 * the atlas port. The roster as a system of record: who is here, what they can
 * do, how they are doing. Deliberately split from Coaching — this screen holds
 * facts, Coaching holds interpretation, and both carry a pointer to the other.
 *
 * Ops: getAdvisorRoster / getAdvisorProfile (REQ-038/039).
 *
 * Held pending the data layer (REQ-100): line of business and today's shift on
 * a roster row, tenure, the performance table's on-time / utilisation / NPS,
 * Export, Add advisor, Assign skill, Edit profile.
 */
import { useMemo, useState } from "react";
import { Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { FilterDropdown, EmptyState } from "../../shell/composites";
import { Drawer } from "../../shell/Overlay";
import { useNav } from "../../shell/router";
import { useViewport } from "../../shell/useResponsive";
import { useWfResource } from "./useWorkforce";
import { getAdvisorRoster, getAdvisorProfile, type AdvisorRow, type AdvisorRosterResponse, type AdvisorProfileResponse } from "./wf-api";
import { hmRangeLabel, WfPending } from "./wf-shared";
import { useT, t as i18nT } from "../../lib/i18n";
import { DemoDataBadge } from "../../lib/provenance";
import { useTenantLabels } from "../../lib/labels";

const STATUS_TONE: Record<AdvisorRow["status"], "success" | "warn" | "info"> = { Active: "success", Probation: "warn", "On leave": "info" };

function AdvisorDrawer({ staffId, onClose, onAdjustShift }: { staffId: string; onClose: () => void; onAdjustShift?: () => void }) {
  const t = useT();
  const { data, loading, error } = useWfResource<AdvisorProfileResponse>(`advisor:${staffId}`, () => getAdvisorProfile(staffId));
  const maxTrend = Math.max(1, ...(data?.perf.trend ?? []).map((x) => x.value));
  const head = (label: string) => (
    <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05, fontWeight: 700, marginBottom: 10 }}>{label}</div>
  );
  return (
    <Drawer panel="wfadvisor" width={620} testid="wfadvisors-drawer" icon={<Icon.user size={16} />}
      title={data?.advisor.name ?? t("workforce.advisors.advisorProfile", "Advisor profile")}
      sub={data ? `${data.advisor.role} · ${data.advisor.branch} · ${t("workforce.advisors.hired", "Hired")} ${data.advisor.hiredLabel}` : undefined}
      onClose={onClose}
      footer={<>
        <Btn variant="secondary" disabled title={t("workforce.advisors.editProfileHeld", "Editing an advisor profile needs REQ-100e")}>{t("workforce.advisors.editProfile", "Edit profile")}</Btn>
        <Btn onClick={onAdjustShift} disabled={!onAdjustShift}>{t("workforce.advisors.adjustShift", "Adjust shift")}</Btn>
      </>}>
      {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.advisors.couldnTLoadTheProfile", "Couldn't load the profile:")}{" "}{error}</div>
        : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
        : <>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ width: 44, height: 44, borderRadius: "50%", background: T.p3, color: T.p1, fontSize: 15, fontWeight: 700, display: "grid", placeItems: "center" }}>{data.advisor.initials}</span>
            <Pill tone={STATUS_TONE[data.advisor.status]}>{data.advisor.status}</Pill>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 14, paddingBottom: 16, borderBottom: `1px solid ${T.n6}` }}>
            {[[t("workforce.advisors.conversion", "Conversion"), `${data.perf.convPct}%`], [t("workforce.advisors.avgDuration", "Avg duration"), `${data.perf.avgDur}m`], [t("workforce.advisors.customerScore", "Customer score"), String(data.perf.csat)], [t("workforce.coaching.appointments", "Appointments"), String(data.perf.apptsN)]].map(([l, v]) => (
              <span key={l} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 11, color: T.soft }}>{l}<DemoDataBadge of={data.perf} style={{ marginLeft: 6 }} /></span>
                <span style={{ fontSize: 17, fontWeight: 700, fontFamily: T.fontMono, color: T.n1 }}>{v}</span>
              </span>
            ))}
          </div>
          {data.perf.trend.length > 0 ? (
            <div style={{ paddingBottom: 16, borderBottom: `1px solid ${T.n6}` }}>
              {head(t("workforce.advisors.trendRecentWeeks", "Trend · recent weeks"))}
              <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 72 }}>
                {data.perf.trend.map((w) => (
                  <span key={w.weekLabel} title={i18nT("workforce.advisors.tWeeklabelTValue.icu", { value0: w.weekLabel, value1: w.value })} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }}>
                    <span style={{ width: "100%", maxWidth: 34, height: `${(w.value / maxTrend) * 100}%`, minHeight: 4, background: T.p1, borderRadius: "3px 3px 0 0", display: "block" }} />
                    <span style={{ fontSize: 9.5, color: T.soft, fontFamily: T.fontMono }}>{w.weekLabel}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <div style={{ paddingBottom: 16, borderBottom: `1px solid ${T.n6}` }}>
            {head(t("workforce.advisors.thisWeek", "This week"))}
            {data.schedule.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.advisors.noPublishedShiftsThisWeek", "No published shifts this week.")}</span> : data.schedule.map((d) => (
              <div key={d.day} style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: `1px solid ${T.n6}`, fontSize: 12.5, alignItems: "baseline" }}>
                <b style={{ width: 90, color: T.n1, flexShrink: 0 }}>{d.day}</b>
                {d.shifts.length === 0 ? <span style={{ color: T.soft }}>{t("workforce.advisors.dayOff", "Day off")}</span> : (
                  <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {d.shifts.map((s) => <span key={s.id} style={{ fontFamily: T.fontMono, fontSize: 11.5, color: T.n1, background: T.n6, padding: "2px 8px", borderRadius: 6 }}>{hmRangeLabel(s.start, s.end)}<span style={{ color: T.soft }}> · {s.role}</span></span>)}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={{ paddingBottom: 16, borderBottom: `1px solid ${T.n6}` }}>
            {head(t("workforce.advisors.skillsCertifications", "Skills & certifications"))}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {data.skillsDetail.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.advisors.noCertifiedSkillsRecorded", "No certified skills recorded.")}</span> : data.skillsDetail.map((s) => (
                <span key={s.skill} style={{ display: "inline-flex", flexDirection: "column", gap: 1, padding: "6px 11px", borderRadius: 8, background: T.n6, border: `1px solid ${T.n5}` }}>
                  <b style={{ fontSize: 12, color: T.n1 }}>{s.skill}</b>
                  <span style={{ fontSize: 10, color: s.expires ? T.orange1 : T.soft, fontFamily: T.fontMono }}>{s.expires ? `${t("workforce.advisors.expires", "expires")} ${s.expires}` : s.certifiedAt ? `${t("workforce.advisors.since", "since")} ${s.certifiedAt}` : t("workforce.advisors.certified", "certified")}</span>
                </span>
              ))}
            </div>
          </div>
          <div>
            {head(t("workforce.advisors.coachingLog", "Coaching log"))}
            {data.coachingLog.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.advisors.noEntriesYet11sTrainingAndNotesFromCoachingAppearHere", "No entries yet — 1:1s, training and notes from Coaching appear here.")}</span> : data.coachingLog.map((e, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${T.n6}` }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: T.p3, color: T.p1, display: "grid", placeItems: "center", flexShrink: 0 }}><Icon.check size={12} /></span>
                <span style={{ flex: 1 }}><b style={{ fontSize: 12.5, color: T.n1, display: "block" }}>{e.title}</b><span style={{ fontSize: 12, color: T.n2 }}>{e.body}</span></span>
                <span style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{e.date}</span>
              </div>
            ))}
          </div>
        </>}
    </Drawer>
  );
}

/** A1/A2 — roster. KPI strip, search, multi-facet filters, then the rows. */
function RosterTab({ data, onPick }: { data: AdvisorRosterResponse; onPick: (id: string) => void }) {
  const t = useT();
  const lbl = useTenantLabels(); // "branch" is tenant vocabulary (lib/labels)
  const phone = useViewport() === "phone";
  const [q, setQ] = useState(""); // ui-local
  const [branch, setBranch] = useState<string | null>(null); // ui-local
  const [role, setRole] = useState<string | null>(null); // ui-local
  const [status, setStatus] = useState<string | null>(null); // ui-local
  const [skill, setSkill] = useState<string | null>(null); // ui-local

  const branches = [...new Set(data.items.map((a) => a.branch))];
  const roles = [...new Set(data.items.map((a) => a.role))];
  const statuses = [...new Set(data.items.map((a) => a.status))];
  const skillLabel = (id: string) => data.services.find((s) => s.id === id)?.label ?? id;
  const skillIdByLabel = (label: string) => data.services.find((s) => s.label === label)?.id ?? label;

  const filtered = data.items.filter((a) =>
    (!q || a.name.toLowerCase().includes(q.toLowerCase()))
    && (!branch || a.branch === branch) && (!role || a.role === role)
    && (!status || a.status === status) && (!skill || a.skills.includes(skillIdByLabel(skill))));

  const active = data.items.filter((a) => a.status === "Active").length;
  const onLeave = data.items.filter((a) => a.status === "On leave").length;
  const avgAdherence = data.items.length ? Math.round(data.items.reduce((s, a) => s + a.adherence, 0) / data.items.length) : 0;
  const cols = "1.7fr 1.2fr 1.2fr 100px 74px 96px 78px";

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr 1fr" : "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {[[t("workforce.advisors.onRoster", "On roster"), String(data.items.length), T.p1],
          [t("workforce.advisors.activeLabel", "Active"), String(active), T.green1],
          [t("workforce.advisors.onLeaveLabel", "On leave"), String(onLeave), T.orange1],
          [t("workforce.advisors.avgAdherence", "Avg adherence"), `${avgAdherence}%`, T.tDarkBlue]].map(([label, value, accent]) => (
          <div key={label} style={{ padding: "12px 15px", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, position: "relative", overflow: "hidden" }}>
            <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.07, color: T.soft, display: "block" }}>{label}</span>
            <span style={{ fontSize: 22, fontWeight: 700, color: T.n1, fontFamily: T.fontMono }}>{value}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("workforce.advisors.searchByName", "Search by name…")}
          style={{ flex: 1, minWidth: 200, maxWidth: 320, padding: "9px 12px", minHeight: 36, border: `1px solid ${T.n5}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" }} />
        <FilterDropdown label={branch ?? `${t("workforce.opsToday.allWord", "All")} ${lbl("branches", "Branches").toLowerCase()}`} active={!!branch} options={branches} onPick={setBranch} onClear={() => setBranch(null)} />
        <FilterDropdown label={role ?? t("workforce.workforceAdvisors.allRoles", "All roles")} active={!!role} options={roles} onPick={setRole} onClear={() => setRole(null)} />
        <FilterDropdown label={skill ?? t("workforce.advisors.allSkills", "All skills")} active={!!skill} options={data.services.map((s) => s.label)} onPick={setSkill} onClear={() => setSkill(null)} />
        <FilterDropdown label={status ?? t("workforce.advisors.allStatuses", "All statuses")} active={!!status} options={statuses} onPick={setStatus} onClear={() => setStatus(null)} />
        <span style={{ flex: 1 }} />
        <Btn size="sm" variant="secondary" disabled icon={<Icon.download size={13} />} title={t("workforce.advisors.exportHeld", "Roster export needs REQ-100d")}>{t("workforce.floor.exportCsv", "Export")}</Btn>
        <Btn size="sm" disabled icon={<Icon.plus size={13} />} title={t("workforce.advisors.addAdvisorHeld", "Inviting an advisor needs REQ-100d")}>{t("workforce.advisors.addAdvisor", "Add Advisor")}</Btn>
      </div>

      <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden", flexShrink: 0 }}>
        {!phone ? (
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, padding: "10px 18px", background: T.n6, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>
            <span>{t("workforce.advisors.advisor", "Advisor")}</span><span>{t("workforce.advisors.branch", "Branch")}</span><span>{t("workforce.advisors.role", "Role")}</span><span>{t("workforce.advisors.status", "Status")}</span><span>{t("workforce.advisors.fte", "FTE")}</span><span>{t("workforce.advisors.adherence", "Adherence")}</span><span />
          </div>
        ) : null}
        {filtered.length === 0 ? <EmptyState icon={<Icon.user size={18} />} title={t("workforce.advisors.noAdvisorsMatch", "No advisors match")} body={t("workforce.advisors.tryADifferentSearchOrFilter", "Try a different search or filter.")} /> : filtered.map((a) => (
          <div key={a.id} data-testid="wfadvisors-row" data-id={a.id} onClick={() => onPick(a.id)}
            onMouseEnter={(e) => (e.currentTarget.style.background = T.p4)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            style={{ display: "grid", gridTemplateColumns: phone ? "1fr auto" : cols, gap: 10, padding: "12px 18px", borderBottom: `1px solid ${T.n6}`, alignItems: "center", fontSize: 12.5, cursor: "pointer", minHeight: 52 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ width: 28, height: 28, borderRadius: "50%", background: T.p3, color: T.p1, fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>{a.initials}</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <b style={{ color: T.n1 }}>{a.name}</b>
                <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.advisors.hired", "Hired")}{" "}{a.hiredLabel}{phone ? ` · ${a.role}` : ""}</span>
              </span>
            </span>
            {!phone ? <span style={{ color: T.n2 }}>{a.branch}</span> : null}
            {!phone ? <span><span style={{ fontSize: 11, fontWeight: 600, color: T.p1, background: T.p4, padding: "3px 8px", borderRadius: 5 }}>{a.role}</span></span> : null}
            {!phone ? <span><Pill tone={STATUS_TONE[a.status]}>{a.status}</Pill></span> : null}
            {!phone ? <span style={{ fontFamily: T.fontMono }}>{a.fte.toFixed(1)}</span> : null}
            <span style={{ fontFamily: T.fontMono, fontWeight: 700, color: a.adherence >= 92 ? T.green1 : a.adherence >= 85 ? T.orange1 : T.red1, textAlign: phone ? "right" : "left" }}>
              {a.adherence}%<DemoDataBadge of={a} style={{ marginLeft: 6 }} />
            </span>
            {!phone ? <span style={{ fontSize: 12, color: T.p1, fontWeight: 600, textAlign: "right" }}>{t("workforce.advisors.profile", "Profile")} ›</span> : null}
          </div>
        ))}
      </div>
      <span style={{ fontSize: 11.5, color: T.soft }}>
        {t("workforce.advisors.rowsHeld", "A row's line of business and today's shift are in the capture but not on the roster response (REQ-100a) — the role badge and adherence are what is served.")}
      </span>
    </>
  );
}

/**
 * A3 — skill matrix. Advisors × lines of business, ticks where trained. The
 * subtitle is the reason it exists: edit a cell and the schedule generator
 * behaves differently. This is configuration wearing a table's clothes.
 */
function SkillMatrixTab({ data }: { data: AdvisorRosterResponse }) {
  const t = useT();
  const rows = useMemo(() => [...data.items].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const perService = useMemo(() => {
    const m: Record<string, number> = {};
    data.services.forEach((s) => { m[s.id] = data.items.filter((a) => a.skills.includes(s.id)).length; });
    return m;
  }, [data]);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden", flexShrink: 0 }}>
      <div style={{ padding: "15px 20px 12px", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <b style={{ fontSize: 14.5, color: T.n1 }}>{t("workforce.advisors.skillMatrix", "Skill matrix")}</b>
          <span style={{ fontSize: 11.5, color: T.soft }}>{t("workforce.advisors.skillMatrixSub", "Used by the optimiser to honour minimum-staffing rules.")}</span>
        </span>
        <Btn size="sm" variant="secondary" disabled icon={<Icon.plus size={13} />} title={t("workforce.advisors.assignSkillHeld", "Assigning a skill needs REQ-100c")}>{t("workforce.advisors.assignSkill", "Assign skill")}</Btn>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: T.n6 }}>
              <th style={{ textAlign: "left", padding: "10px 18px", fontSize: 10, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, position: "sticky", left: 0, background: T.n6 }}>{t("workforce.advisors.advisor", "Advisor")}</th>
              {data.services.map((s) => (
                <th key={s.id} style={{ padding: "10px 8px", fontSize: 10, color: T.soft, minWidth: 92, fontWeight: 700 }}>
                  {s.label}
                  <span style={{ display: "block", fontFamily: T.fontMono, fontSize: 10.5, color: perService[s.id] < 2 ? T.red1 : T.n2, fontWeight: 700 }}>{perService[s.id]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} data-testid="wfadvisors-skill-row">
                <td style={{ padding: "10px 18px", borderTop: `1px solid ${T.n6}`, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", position: "sticky", left: 0, background: T.white }}>
                  {a.name}<span style={{ display: "block", fontSize: 10.5, color: T.soft, fontWeight: 400 }}>{a.branch}</span>
                </td>
                {data.services.map((s) => {
                  const has = a.skills.includes(s.id);
                  return (
                    <td key={s.id} style={{ padding: "10px 8px", borderTop: `1px solid ${T.n6}`, textAlign: "center", background: has ? alpha(T.green1, 7) : "transparent" }}>
                      {has ? <Icon.check size={14} style={{ color: T.green1 }} /> : <span style={{ color: T.n4 }}>·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "10px 18px", fontSize: 11, color: T.soft, borderTop: `1px solid ${T.n6}` }}>
        {t("workforce.advisors.skillMatrixFoot", "The count under each line of business is how many advisors can serve it; red means fewer than two, which is a single absence away from no coverage.")}
      </div>
    </div>
  );
}

export default function WorkforceAdvisors() {
  const t = useT();
  const nav = useNav();
  const { data, loading, error } = useWfResource("wfadvisors", getAdvisorRoster);
  const [tab, setTab] = useState<"roster" | "reqs" | "skills" | "perf">("roster"); // ui-local
  const [profileId, setProfileId] = useState<string | null>(null);
  const TABS: { id: typeof tab; label: string }[] = [
    { id: "roster", label: t("workforce.advisors.roster", "Roster") },
    { id: "reqs", label: t("workforce.advisors.requisitions", "Requisitions") },
    { id: "skills", label: t("workforce.advisors.skillMatrix", "Skill matrix") },
    { id: "perf", label: t("workforce.advisors.performance", "Performance") },
  ];
  return (
    <Screen rail="wf-advisors">
      <DataSourceStrip screen="wfadvisors" ops={["getAdvisorRoster", "getAdvisorProfile"]} reqId="REQ-038/039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 4, background: T.n6, borderRadius: 9, padding: 3, alignSelf: "flex-start", flexWrap: "wrap" }}>
          {TABS.map((x) => (
            /* ui-raw: single-select strip, brand-filled active segment — SegmentedControl's pattern (group 8) */
            <button key={x.id} onClick={() => setTab(x.id)} data-testid={`wfadvisors-tab-${x.id}`}
              style={{ padding: "7px 15px", minHeight: 34, borderRadius: 7, border: "none", background: tab === x.id ? T.p1 : "transparent", color: tab === x.id ? T.white : T.soft, fontSize: 12.5, fontWeight: tab === x.id ? 700 : 600, cursor: "pointer", fontFamily: T.font }}>{x.label}</button>
          ))}
        </div>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.advisors.couldnTLoadAdvisors", "Couldn't load advisors:")}{" "}{error}</div>
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : tab === "roster" ? <RosterTab data={data} onPick={setProfileId} />
          : tab === "skills" ? <SkillMatrixTab data={data} />
          : tab === "perf" ? (
            <>
              {/* A4 — this screen declares itself the source of truth for the
                  numbers, and points coaching questions at the other one. */}
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "11px 15px", borderRadius: 9, background: T.p4 }}>
                <Icon.info size={14} style={{ color: T.p1, flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
                  <b style={{ color: T.n1 }}>{t("workforce.advisors.sourceOfTruth", "The source of truth for the numbers.")}</b>{" "}
                  {t("workforce.advisors.coachingQuestionsGo", "Coaching questions belong in")}{" "}
                  {/* ui-raw: link inside a running sentence — Btn's link variant is inline-flex with padding and cannot sit in a text run */}
                  <button onClick={() => nav.go("workforce.coaching")} style={{ border: "none", background: "none", padding: 0, color: T.p1, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: T.font }}>{t("workforce.advisors.coachingPerformanceGrid", "Coaching → Performance grid")}</button>{" "}
                  {t("workforce.advisors.twoScreensOnPurpose", "— two performance screens on purpose: facts here, interpretation there.")}
                </span>
              </div>
              <WfPending opName="getAdvisorPerformanceTable" screen="wfadvisors" />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, color: T.n2 }}>{t("workforce.advisors.reqsEmbedPending", "Requisitions is the same component at a different scope; embedding it here lands with the route consolidation in stage 9.")}</span>
              <Btn size="sm" variant="link" onClick={() => nav.go("workforce.requisitions")}>{t("workforce.advisors.openRequisitions", "Open Requisitions →")}</Btn>
            </div>
          )}
      </div>
      {profileId ? <AdvisorDrawer staffId={profileId} onClose={() => setProfileId(null)} onAdjustShift={() => { setProfileId(null); nav.go("workforce.schedule"); }} /> : null}
    </Screen>
  );
}
